// iter283 — Agent Invitation Landing (public, read-only).
//
// Scope guarantee: this page ONLY validates the invitation token via
// the existing GET /api/agent-invitations/{token}/validate endpoint
// and renders the masked record. Acceptance (OTP + property_users
// write) is explicitly out of scope — same posture as the backend
// service. The page labels that step as "coming soon" so operators
// don't mistake validation for activation.
//
// This file is NEW. It does not import, modify, or alias any existing
// invite / add-user / auth / permission module.

import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";

const API_BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");

export default function AgentInvitationLanding() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, data: null, error: "" });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!token) {
        setState({ loading: false, data: null, error: "Invalid link." });
        return;
      }
      try {
        const resp = await fetch(
          `${API_BASE}/api/agent-invitations/${encodeURIComponent(token)}/validate`,
          { method: "GET", headers: { "Content-Type": "application/json" } },
        );
        const body = await resp.json().catch(() => ({}));
        if (cancelled) return;
        if (!resp.ok || !body || body.STATUS !== 1 || !body.RESULT) {
          setState({
            loading: false,
            data: null,
            error:
              (body && (body.MESSAGE || body.detail)) ||
              "This invitation link is invalid, expired, or has already been used.",
          });
          return;
        }
        setState({ loading: false, data: body.RESULT, error: "" });
      } catch (e) {
        if (cancelled) return;
        setState({
          loading: false,
          data: null,
          error: "We couldn't reach the server. Please retry in a moment.",
        });
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [token]);

  return (
    <div
      className="min-h-screen bg-slate-50 flex items-center justify-center p-6"
      data-testid="agent-invitation-landing"
    >
      <div className="w-full max-w-md bg-white rounded-2xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center gap-2 mb-4">
          <div className="w-9 h-9 rounded-full bg-[#1B2A7B]/10 flex items-center justify-center">
            <span className="text-[#1B2A7B] font-bold">S</span>
          </div>
          <div>
            <div className="text-[11px] uppercase tracking-wider text-slate-500">
              Superhost
            </div>
            <div className="text-sm font-semibold text-slate-800">
              Agent Invitation
            </div>
          </div>
        </div>

        {state.loading && (
          <div className="py-8 text-center" data-testid="agent-invitation-loading">
            <div className="inline-block w-6 h-6 border-2 border-[#1B2A7B]/30 border-t-[#1B2A7B] rounded-full animate-spin" />
            <div className="mt-3 text-sm text-slate-600">
              Checking your invitation…
            </div>
          </div>
        )}

        {!state.loading && state.error && (
          <div
            className="rounded-xl bg-rose-50 border border-rose-200 p-4"
            data-testid="agent-invitation-error"
          >
            <div className="text-sm font-semibold text-rose-800">
              Invitation unavailable
            </div>
            <div className="text-xs text-rose-700 mt-1">{state.error}</div>
          </div>
        )}

        {!state.loading && !state.error && state.data && (
          <div data-testid="agent-invitation-detail">
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-4 mb-4">
              <div className="text-sm font-semibold text-emerald-800">
                You've been invited to join as{" "}
                {state.data.role_display || state.data.role || "an agent"}
              </div>
              <div className="text-xs text-emerald-700 mt-1">
                Expires {formatRelative(state.data.expires_at)} ·
                status {state.data.status}
              </div>
            </div>

            <dl className="divide-y divide-slate-100 text-sm">
              {state.data.full_name_masked ? (
                <Row label="Name" value={state.data.full_name_masked} />
              ) : null}
              {state.data.phone_masked ? (
                <Row label="Phone" value={state.data.phone_masked} />
              ) : null}
              {state.data.property_name_masked ? (
                <Row label="Property" value={state.data.property_name_masked} />
              ) : null}
              {Array.isArray(state.data.allowed_tabs) && state.data.allowed_tabs.length ? (
                <Row
                  label="Access"
                  value={state.data.allowed_tabs.join(" · ")}
                />
              ) : null}
            </dl>

            <div
              className="mt-5 rounded-xl bg-amber-50 border border-amber-200 p-3"
              data-testid="agent-invitation-pending-activation"
            >
              <div className="text-xs font-semibold text-amber-800">
                Acceptance is coming soon
              </div>
              <div className="text-[11px] text-amber-700 mt-1">
                Your invitation is validated, but OTP verification and
                account activation are not yet live on this landing page.
                You'll receive a follow-up WhatsApp once activation is
                enabled.
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 text-[11px] text-slate-400 text-center">
          Powered by Superhost · thesuperhost.app
        </div>
      </div>
    </div>
  );
}

function Row({ label, value }) {
  return (
    <div className="py-2 flex items-center justify-between gap-4">
      <dt className="text-[11px] uppercase tracking-wider text-slate-500">
        {label}
      </dt>
      <dd className="text-sm text-slate-800 font-medium text-right">
        {value}
      </dd>
    </div>
  );
}

function formatRelative(iso) {
  if (!iso) return "soon";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "soon";
  const diffMs = d.getTime() - Date.now();
  if (diffMs <= 0) return "already expired";
  const h = Math.floor(diffMs / 3_600_000);
  if (h < 1) return "in under an hour";
  if (h < 24) return `in ~${h} hour${h === 1 ? "" : "s"}`;
  const days = Math.floor(h / 24);
  return `in ~${days} day${days === 1 ? "" : "s"}`;
}
